import type { Obstacle } from './Collision';

/**
 * Coarse spatial hash for static obstacles (grass tufts). Turns a 4 000-tuft
 * linear scan into a 3×3 cell lookup per sim entity per frame.
 */
export class ObstacleGrid {
  private readonly cells: Map<number, Obstacle[]> = new Map();
  private readonly cellSize: number;

  constructor(obstacles: readonly Obstacle[], cellSize = 1.0) {
    this.cellSize = cellSize;
    for (const o of obstacles) {
      const key = this.keyFor(o.x, o.z);
      let bin = this.cells.get(key);
      if (!bin) { bin = []; this.cells.set(key, bin); }
      bin.push(o);
    }
  }

  /** Obstacles in the 3×3 block of cells around (x, z). Returned array is
   *  shared — callers must treat it as readonly and use within the frame. */
  near(x: number, z: number): readonly Obstacle[] {
    const cx = Math.floor(x / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    const out: Obstacle[] = [];
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const bin = this.cells.get((cx + dx) * 73856093 ^ (cz + dz) * 19349663);
        if (bin) for (const o of bin) out.push(o);
      }
    }
    return out;
  }

  private keyFor(x: number, z: number): number {
    const cx = Math.floor(x / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    return cx * 73856093 ^ cz * 19349663;
  }
}
